import { getRequestHeaders } from "./headers";
import { handleError, apiFetch, API_BASE } from './api';

// --- Types ---

export interface Transcript {
    id: string;
    trainingId: string;
    status: string;
    statusDatetime: string;
    dueDate: string;
}

export interface TranscriptsResponse {
    items: Transcript[];
}

export interface TrainingInfo {
    learn_training_id: string;
    multilingual_attributes: {
        is_default_language: boolean;
        training_title: string;
    }[];
}

export interface BatchTrainingsResponse {
    trainingList: TrainingInfo[];
    errorTrainingList: { learn_training_id: string; error_type: string }[];
}

export interface LearnTrainingCompKeyInput {
    learn_training_id: string;
}

// --- Combined type for UI ---

export interface AssignedTraining {
    id: string;
    trainingId: string;
    title: string;
    status: string;
    statusDatetime: string;
    dueDate: string;
}

// --- API calls ---

/**
 * Fetch top-level transcripts for a given alias.
 * This calls our backend which proxies to the Learn AppSync API.
 */
export async function fetchTranscriptsByAlias(alias: string): Promise<TranscriptsResponse> {
    const res = await apiFetch(`${API_BASE}/learn/transcripts?alias=${encodeURIComponent(alias)}`, {
        headers: getRequestHeaders(),
    });
    if (!res.ok) await handleError(res);
    return await res.json();
}

/**
 * Fetch training details for a batch of training IDs.
 * This calls our backend which proxies to the Learn AppSync API.
 */
export async function fetchTrainingDetails(trainingIds: string[]): Promise<BatchTrainingsResponse> {
    const res = await apiFetch(`${API_BASE}/learn/trainings`, {
        method: 'POST',
        headers: getRequestHeaders('application/json'),
        body: JSON.stringify({
            inputTrainingList: trainingIds.map(id => ({ learn_training_id: id })),
        }),
    });
    if (!res.ok) await handleError(res);
    return await res.json();
}

/**
 * Fetches transcripts and enriches them with training titles.
 * Returns a combined list of AssignedTraining objects.
 */
export async function fetchAssignedTrainings(alias: string): Promise<AssignedTraining[]> {
    const transcripts = await fetchTranscriptsByAlias(alias);

    if (!transcripts.items || transcripts.items.length === 0) {
        return [];
    }

    // Get unique training IDs
    const trainingIds = Array.from(new Set(transcripts.items.map(t => t.trainingId)));

    // Try to fetch training details — gracefully degrade if unavailable
    let titleMap = new Map<string, string>();
    try {
        const details = await fetchTrainingDetails(trainingIds);
        for (const training of details.trainingList) {
            const defaultAttr = training.multilingual_attributes.find(a => a.is_default_language);
            const title = defaultAttr?.training_title || training.multilingual_attributes[0]?.training_title || 'Untitled';
            titleMap.set(training.learn_training_id, title);
        }
    } catch {
        // batchGetTrainings may not be available — fall back to trainingId as title
    }

    // Combine transcripts with titles
    return transcripts.items.map(t => ({
        id: t.id,
        trainingId: t.trainingId,
        title: titleMap.get(t.trainingId) || t.trainingId,
        status: t.status,
        statusDatetime: t.statusDatetime?.split('@')[1] || t.statusDatetime,
        dueDate: t.dueDate,
    }));
}
